import React, { useState, useEffect } from "react";
import { Download, KeyRound, Copy, Loader2, Library as LibraryIcon } from "lucide-react";
import api, { assetImage, downloadUrl } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

export default function Library() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/library")
      .then((r) => setItems(r.data.items))
      .finally(() => setLoading(false));
  }, []);

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast.success("License key copied");
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 size={28} className="animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-10">
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">{t("library.title")}</h1>
      <p className="mt-1 text-sm text-slate-500">{t("library.subtitle")}</p>

      {items.length === 0 ? (
        <div className="mt-16 flex flex-col items-center text-center">
          <LibraryIcon size={48} className="text-slate-200" />
          <p className="mt-4 text-lg font-semibold text-slate-900">{t("library.empty")}</p>
          <p className="mt-1 text-sm text-slate-500">{t("library.emptyDesc")}</p>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <div
              key={item.asset_id}
              className="overflow-hidden rounded-2xl border border-slate-200 bg-white"
              data-testid={`library-item-${item.asset_id}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
                <img src={assetImage(item)} alt="" className="h-full w-full object-cover" />
                <span className="absolute top-3 left-3 rounded-md bg-emerald-500 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                  {t(`detail.${item.license}`)}
                </span>
              </div>
              <div className="p-4">
                <h3 className="line-clamp-1 font-semibold text-slate-900">
                  {lang === "id" ? item.title_id : item.title_en}
                </h3>
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
                  <KeyRound size={13} className="text-slate-400" />
                  <span className="flex-1 truncate font-mono text-xs text-slate-600">{item.license_key}</span>
                  <button
                    onClick={() => copyKey(item.license_key)}
                    className="text-slate-400 hover:text-slate-900"
                    data-testid={`copy-key-${item.asset_id}`}
                  >
                    <Copy size={13} />
                  </button>
                </div>
                <a
                  href={downloadUrl(item.asset_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 transition"
                  data-testid={`download-btn-${item.asset_id}`}
                >
                  <Download size={15} /> {t("library.download")}
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
